import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { startCase } from "lodash";
import { AuditLog } from "../../../../services/business.service";

export interface AuditLogTableData extends AuditLog {
  _id: string;
}

interface AuditLogDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  auditLog: AuditLogTableData | null;
}

export const AuditLogDetailsDialog: React.FC<AuditLogDetailsDialogProps> = ({
  open,
  onClose,
  auditLog,
}) => {
  if (!auditLog) return null;

  const formatDetails = (details: string | object) => {
    if (typeof details === "string") {
      try {
        return JSON.stringify(JSON.parse(details), null, 2);
      } catch {
        return details;
      }
    }
    return JSON.stringify(details, null, 2);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
          color: "white",
          position: "relative",
          py: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, pr: 6 }}>
          Audit Log Details
        </Typography>
        <Typography
          variant="body2"
          sx={{ opacity: 0.8, mt: 0.5, fontSize: "0.875rem" }}
        >
          {new Date(auditLog.timestamp).toLocaleString()}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "white",
            "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <ViewIcon fontSize="small" />
                  Basic Information
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Log ID
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                    >
                      {auditLog.log_id}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      User Email
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {auditLog.user_email}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Module
                    </Typography>
                    <Chip
                      label={startCase(auditLog.module)}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Action
                    </Typography>
                    <Chip
                      label={startCase(auditLog.action)}
                      size="small"
                      color="secondary"
                      variant="filled"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: "info.main",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FilterIcon fontSize="small" />
                  Additional Info
                </Typography>
                <Stack spacing={2}>
                  {auditLog.user_id && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        User ID
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                      >
                        {auditLog.user_id}
                      </Typography>
                    </Box>
                  )}
                  {auditLog.store_id && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Store ID
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                      >
                        {auditLog.store_id}
                      </Typography>
                    </Box>
                  )}
                  {auditLog.branch_id && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Branch ID
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                      >
                        {auditLog.branch_id}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Source
                    </Typography>
                    <Chip
                      label={auditLog.is_system_generated ? "System" : "User"}
                      size="small"
                      color={auditLog.is_system_generated ? "default" : "info"}
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  {auditLog.document_type && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Document Type
                      </Typography>
                      <Typography variant="body2">
                        {auditLog.document_type}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {auditLog.details && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: "warning.main",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <SearchIcon fontSize="small" />
                    Details
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: "grey.50",
                      borderRadius: 1,
                      p: 2,
                      border: "1px solid",
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography
                      component="pre"
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        margin: 0,
                        maxHeight: 300,
                        overflow: "auto",
                      }}
                    >
                      {formatDetails(auditLog.details)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} variant="outlined" size="large">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
